/* eslint-disable no-prototype-builtins */
import { useState, useEffect, Fragment, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import * as _ from 'lodash';
import { Col, Row, Container, Form } from 'react-bootstrap';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import MatchCard from 'components/general/match_card.js';
import {
  getSportTypeToActiveIcon,
  getSportTypeToWhiteIcon,
  sportIdToSportType,
  sportTypetToSportIdString,
  getDeviceType
} from 'toolkit/utils';
import SportDetailItem from 'components/general/SportDetailItem.js';
import { updateMatch } from 'stores/match';
import {
  fetchLiveMatches,
  setNextLiveMatches,
  setSelectedLeague,
  setSelectedSport
} from 'stores/match';
import eventsToGroup from 'toolkit/eventsToGroup.js';
import axios from 'axios';
import {
  TopLeaguesImages,
  TMM_URL,
  calculateSportCount,
  AWS_BUCKET_URL,
  tournamentGroups,
  tournamentNames
} from 'toolkit/constants.js';
import { fetchBalance } from 'stores/auth';
import {
  setLeagueMatches,
  setNextPageOnProgress,
  setPage,
  setPreMatches,
  setSports,
  setSport,
  setLiveSports
} from 'stores/app';
import { useAllowedSports } from 'toolkit/useHooks/useAllowedSports';

function Start() {
  const dispatch = useDispatch();
  const selectedSport = useSelector((state) => state.match.selectedSport);
  const selectedLeague = useSelector((state) => state.match.selectedLeague);
  const selectedLocale = useSelector((state) => state.match.selectedLocale);
  const locales = useSelector((state) => state.match.locales);
  const counts = useSelector((state) => state.match.counts);
  const events = useSelector((state) => state.match.events);
  const { user } = useSelector((state) => state.auth);
  const { isSportAllowed } = useAllowedSports();
  const liveEvents = useSelector((state) => {
    const { liveMatches, events } = state.match;
    return liveMatches.map((c) => events[c]);
  }, shallowEqual);

  const { t } = useTranslation('translations', { useSuspense: false });
  const [lastScrollPosition, setLastScrollPosition] = useState(
    localStorage.getItem('scrollPosition') || 0
  );
  const [selectedMarketType, setSelectedMarketType] = useState(7);
  const location = useLocation();
  const { page, nextPageOnProgress, preMatches, leagueMatches, sports, sport, liveSports } =
    useSelector((state) => state.app);

  function getCounts() {
    let s = Object.keys(counts);
    dispatch(setSports(s));
    if (s.length > 0) dispatch(setSport(sportTypetToSportIdString(s[0])));
  }

  function getPreMatches(page, sportIds) {
    const sportNew = sportIds ? sportIds : sport;
    axios
      .post(`${TMM_URL}/BetEvent/GetEventsBySportByPagination`, {
        pageSize: 20,
        page: page,
        sport: sportNew,
        hideLiveMatches: true
      })
      .then((result) => {
        let filteredMatches = result.data.data.betEvents.map((m) => {
          return { ...m, markets: m.markets.filter((m) => m.outcomes.length > 0) };
        });
        // events
        const data = { events: filteredMatches };
        dispatch(updateMatch(data));

        let finalArray = [];
        let topMatchDateEvents = _.groupBy(filteredMatches, (m) => m.gameDate);
        let dateKeys = Object.keys(topMatchDateEvents);
        dateKeys.sort((m1, m2) => new Date(m1) - new Date(m2));
        for (let i = 0; i < dateKeys.length; i++) {
          let leagueGroups = _.groupBy(topMatchDateEvents[dateKeys[i]], (m) => m.leagueId);
          for (let leagueGroupsKey in leagueGroups) {
            //finalArray = finalArray.concat(leagueGroups[leagueGroupsKey]);
            let league = leagueGroups[leagueGroupsKey][0].league;
            let category = leagueGroups[leagueGroupsKey][0].category;
            let categoryId = leagueGroups[leagueGroupsKey][0].categoryId;
            finalArray.push({
              category,
              league,
              categoryId,
              matches: leagueGroups[leagueGroupsKey].map((m) => m.gameNumber)
            });
          }
        }

        if (page === 0) dispatch(setPreMatches(finalArray));
        else dispatch(setPreMatches(preMatches.concat(finalArray)));

        if (result.data.data.betEvents.length === 20) {
          dispatch(setPage(page + 1));
          dispatch(setNextPageOnProgress(false));
        }
      })
      .catch(() => {
        dispatch(setNextPageOnProgress(false));
      });
  }

  function getLeagueMatches() {
    axios
      .post(`${TMM_URL}/BetEvent/GetEventsByTournamentWithHours`, {
        hours: 0,
        tournament: selectedLeague
      })
      .then((result) => {
        let filteredMatches = result.data.data.betEvents
          .sort((a, b) => {
            const dateA = new Date(a.gameDate) ?? 0;
            const dateB = new Date(b.gameDate) ?? 0;
            return dateA - dateB;
          })
          .map((m) => {
            return { ...m, markets: m.markets.filter((m) => m.outcomes.length > 0) };
          });
        const data = { events: filteredMatches };
        dispatch(updateMatch(data));

        let category = filteredMatches[0]?.category;
        let league = filteredMatches[0]?.league;
        dispatch(
          setLeagueMatches([
            { league, category, matches: filteredMatches.map((m) => m.gameNumber) }
          ])
        );
      })
      .catch(() => {});
  }

  function nextPage(p) {
    if (nextPageOnProgress) return;
    dispatch(setNextPageOnProgress(true));
    getPreMatches(p);
  }

  const selectLeague = (e) => {
    const league = 'sr:tournament:' + e.target.value;
    dispatch(setSelectedLeague(league));
  };

  const selectSport = (id) => {
    dispatch(setSport(id));
    dispatch(setSelectedLeague(''));
    dispatch(setNextLiveMatches());
    getPreMatches(0, id);
  };

  const lastScrollRef = useRef(lastScrollPosition);

  useEffect(() => {
    lastScrollRef.current = lastScrollPosition;
  }, [lastScrollPosition]);

  const handleScroll = useCallback(() => {
    const container = window;
    const stickyEl = document.querySelector('.tip-types');
    if (!stickyEl) return;
    const scrollTop = container.scrollY;
    const direction = scrollTop < lastScrollRef.current ? 'up' : 'down';
    setLastScrollPosition(scrollTop);

    if (scrollTop > 110) {
      stickyEl.classList.add('fixed');
      if (direction === 'down') {
        if (scrollTop > 200) {
          stickyEl.classList.remove('fixed');
        }
      } else {
        if (scrollTop > 200) {
          stickyEl.classList.add('fixed');
        }
      }
    } else {
      stickyEl.classList.remove('fixed');
    }
  }, []);

  function calculateMenuItemsDynamic() {
    const menuItems = [];
    let icon = '';
    if (user && user.isCasinoEnabled) {
      const casinoTypes = user.casinoTypeInfo || [];
      const vivoInfo = casinoTypes.find((c) => c.name === 'Vivo');
      const gapiInfo = casinoTypes.find((c) => c.name === 'Gapi');
      const isVivoAllowed = vivoInfo ? vivoInfo.isAllowed : true;
      const isGapiAllowed = gapiInfo ? gapiInfo.isAllowed : true;

      if (isVivoAllowed) {
        // push casino icons
        let casinoIcon = (
          <Link to={{ pathname: '/casinobase' }} className="sport-item" key={'live-casino'}>
            <div className={'icon-size'}>
              <img
                width="25"
                height="25"
                className="icon-color"
                src={`${AWS_BUCKET_URL}/Icons/liveicon.svg`}
              />
            </div>
            <div>
              <span className={'sport-item-text'} style={{ color: 'white' }}>
                {t('live') + t('casino')}
              </span>
            </div>
          </Link>
        );
        if (getDeviceType() === 'iphone') {
          casinoIcon = (
            <Link to={{ pathname: '/casinobase' }} className="sport-item" key={'live-casino'}>
              <div className={'icon-size'}>
                <img
                  width="25"
                  height="25"
                  className="icon-color"
                  src={`${AWS_BUCKET_URL}/Icons/liveicon.svg`}
                />
              </div>
              <div>
                <span className={'sport-item-text'} style={{ color: 'white' }}>
                  {t('live') + t('casino')}
                </span>
              </div>
            </Link>
          );
        }
        menuItems.push(casinoIcon);
      }

      if (isGapiAllowed) {
        menuItems.push(
          <Link to={{ pathname: '/slot' }} className="sport-item" key={'slot'}>
            <div className={'icon-size'}>
              <img
                width="25"
                height="25"
                className="icon-color"
                src={`${AWS_BUCKET_URL}/Icons/sloticon.svg`}
              />
            </div>
            <div>
              <span className={'sport-item-text'} style={{ color: 'white' }}>
                {t('slot')}
              </span>
            </div>
          </Link>
        );
      }
    }

    Object.keys(sports).map((sportType, index) => {
      if (sportType === sport) {
        icon = getSportTypeToActiveIcon(sport);
      } else {
        icon = getSportTypeToWhiteIcon(sportType);
      }
      if (
        isSportAllowed(sportType) &&
        counts[sportIdToSportType(sportType)] &&
        Object.keys(counts[sportIdToSportType(sportType)]).length
      ) {
        menuItems.push(
          <SportDetailItem
            key={index}
            sportType={sportType}
            selectedSportType={sport}
            active={sport === sportType}
            count={calculateSportCount(counts, sportIdToSportType(sportType))}
            icon={icon}
            onClick={() => {
              selectSport(sportType);
            }}
            text={t('sports.' + sportType)}
          />
        );
      }
    });

    return menuItems;
  }

  function getBetType() {
    const betTypes = [
      {
        label: t('rom'),
        id: 7
      },
      {
        label: t('winner'),
        id: 1
      },
      {
        label: t('overunder'),
        id: 18
      },
      {
        label: t('handicap'),
        id: 14
      },
      {
        label: t('nextGoal'),
        id: 8
      },
      {
        label: t('bs'),
        id: 29
      },
      
      {
        label: t('doubleChance'),
        id: 10
      }

    ];
    return (
      <div className="tip-types shadow">
        {betTypes.map((betType) => (
          <div className="tip-type" key={betType.id}>
            <button
              className={`btn button btn-sm w-100 border fs-12 ${
                selectedMarketType === betType.id ? 'selected' : ''
              }`}
              onClick={() => setSelectedMarketType(betType.id)}>
              {betType.label}
            </button>
          </div>
        ))}
      </div>
    );
  }

  function getBets(matches) {
    const matchCards = [];
    const liveMatchIds = [];
    const favoriteMatchIds = new Set();
    const favoriteMatchesByLeague = {};
    
    // Collect all favorite matches grouped by league
    // Collect favorites from live matches
    if (liveSports[sport] && liveSports[sport].length > 0 && selectedLeague === '') {
      liveSports[sport]
        .filter((l) => l.matches.length > 0)
        .forEach((liveMatch) => {
          liveMatch.matches.forEach((match) => {
            if (events[match.providerId]?.isFavorite) {
              favoriteMatchIds.add(match.providerId);
              const leagueKey = liveMatch.category + ' / ' + liveMatch.league;
              if (!favoriteMatchesByLeague[leagueKey]) {
                favoriteMatchesByLeague[leagueKey] = {
                  category: liveMatch.category,
                  league: liveMatch.league,
                  categoryId: match.categoryId,
                  matches: []
                };
              }
              favoriteMatchesByLeague[leagueKey].matches.push(match);
            }
          });
        });
    }
    
    // Collect favorites from pre-match matches
    matches.forEach((prematch) => {
      prematch.matches.forEach((matchId) => {
        if (events[matchId]?.isFavorite) {
          favoriteMatchIds.add(matchId);
          const matchEvent = events[matchId];
          if (matchEvent) {
            const leagueKey = prematch.category + ' / ' + prematch.league;
            if (!favoriteMatchesByLeague[leagueKey]) {
              favoriteMatchesByLeague[leagueKey] = {
                category: prematch.category,
                league: prematch.league,
                categoryId: prematch.categoryId,
                matches: []
              };
            }
            favoriteMatchesByLeague[leagueKey].matches.push({ providerId: matchId, ...matchEvent });
          }
        }
      });
    });
    
    // Display favorites grouped by league at the top
    Object.keys(favoriteMatchesByLeague).forEach((leagueKey) => {
      const favoriteGroup = favoriteMatchesByLeague[leagueKey];
      if (favoriteGroup.matches.length > 0) {
        let code = null;
        if (favoriteGroup.categoryId) {
          code = favoriteGroup.categoryId.split(':')[2];
        }
        
        matchCards.push(
          <Fragment key={'favorite-' + leagueKey}>
            <div className="league-names pt-0">
              {code && (
                <img
                  width="20"
                  height="13"
                  src={`${AWS_BUCKET_URL}/ClientFlags/${code}.svg`}
                  alt={code}
                  className="me-2"
                />
              )}
              {favoriteGroup.category + ' / ' + favoriteGroup.league}
            </div>
            {favoriteGroup.matches.map((match, index) => {
              const matchId = match.providerId || match;
              if (liveSports[sport] && liveSports[sport].length > 0 && selectedLeague === '') {
                liveMatchIds.push(matchId);
              }
              return (
                <MatchCard
                  key={matchId}
                  providerId={matchId}
                  marketType={selectedMarketType}
                  index={index}
                  sport={sport}
                  path={location.pathname}
                  locales={locales}
                  selectedLocale={selectedLocale}
                />
              );
            })}
          </Fragment>
        );
      }
    });
    
    // Display live matches (excluding favorites)
    if (liveSports[sport] && liveSports[sport].length > 0 && selectedLeague === '') {
      liveSports[sport]
        .filter((l) => l.matches.length > 0)
        .forEach((liveMatch, index2) => {
          // Filter out favorites
          const nonFavoriteMatches = liveMatch.matches.filter(
            (match) => !favoriteMatchIds.has(match.providerId)
          );
          
          if (nonFavoriteMatches.length === 0) return;
          
          let code = null;
          if (nonFavoriteMatches && nonFavoriteMatches.length > 0 && nonFavoriteMatches[0].categoryId) {
            code = nonFavoriteMatches[0].categoryId.split(':')[2];
          }
          matchCards.push(
            <Fragment key={nonFavoriteMatches[0]?.providerId || liveMatch.matches[0].providerId}>
              {nonFavoriteMatches.length > 0 && (
                <div className="league-names pt-0">
                {code && (
                  <img
                    width="20"
                    height="13"
                    src={`${AWS_BUCKET_URL}/ClientFlags/${code}.svg`}
                    alt={code}
                    className="me-2"
                  />
                )}
                  {liveMatch.category + ' / ' + liveMatch.league}
                </div>
              )}
              {nonFavoriteMatches.map(
                (match, index) => (
                  liveMatchIds.push(match.providerId),
                  (
                    <>
                      <MatchCard
                        key={match.providerId}
                        providerId={match.providerId}
                        marketType={selectedMarketType}
                        index={index}
                        sport={sport}
                        path={location.pathname}
                        locales={locales}
                        selectedLocale={selectedLocale}
                      />
                    </>
                  )
                )
              )}
            </Fragment>
          );
        });
    }

    // Display pre-match matches (excluding favorites)
    matches.forEach((prematch) => {
      let code = null;
      if (prematch.categoryId) {
        code = prematch.categoryId.split(':')[2];
      }
      
      // Filter out favorites and live matches
      const nonFavoritePrematchMatches = prematch.matches
        .filter((obj) => !liveMatchIds.includes(obj) && !favoriteMatchIds.has(obj))
        .sort((a, b) => {
          // Sort by date
          const dateA = events[a]?.gameDate ? new Date(events[a].gameDate).getTime() : 0;
          const dateB = events[b]?.gameDate ? new Date(events[b].gameDate).getTime() : 0;
          return dateA - dateB;
        });
      
      if (nonFavoritePrematchMatches.length === 0) return;
      
      matchCards.push(
        <Fragment key={prematch.league + (nonFavoritePrematchMatches[0] || prematch.matches[0])}>
          {prematch.category && nonFavoritePrematchMatches.length > 0 && (
            <div className="league-names pt-0">
              {code && (
                <img
                  width="20"
                  height="13"
                  src={`${AWS_BUCKET_URL}/ClientFlags/${code}.svg`}
                  alt={code}
                  className="me-2"
                />
              )}
              {prematch.category + ' / ' + prematch.league}</div>
          )}
          {nonFavoritePrematchMatches.map((match, index) => (
              <>
                <MatchCard
                  key={match}
                  providerId={match}
                  marketType={selectedMarketType}
                  index={index}
                  path={location.pathname}
                  locales={locales}
                  selectedLocale={selectedLocale}
                />
              </>
            ))}
        </Fragment>
      );
    });
    return matchCards;
  }
  function leagueSelected(leagueId) {
    dispatch(setSelectedLeague(leagueId));
    dispatch(setSport('0'));
  }

  function checkSportTypes(sport) {
    if (
      sport === '1' ||
      sport === '2' ||
      sport === '4' ||
      sport === '3' ||
      sport === '6' ||
      sport === '12' ||
      sport === '13' ||
      sport === '5' ||
      sport === '11' ||
      sport === '14'
    ) {
      return true;
    }
    return false;
  }

  function getBetTypeTitle() {
    if (selectedMarketType === 29) {
      return (
        <Row>
          <Col xs={4} className="textTitle"></Col>
          <Col xs={4} className="textTitle">
            Yes
          </Col>
          <Col xs={4} className="textTitle" style={{ display: 'flex' }}>
            No
          </Col>
        </Row>
      );
    }
    if (selectedMarketType === 8) {
      return (
        <Row>
          <Col xs={4} className="textTitle">
            1
          </Col>
          <Col xs={4} className="textTitle">
            X
          </Col>
          <Col xs={4} className="textTitle">
            2
          </Col>
        </Row>
      );
    }
    if (selectedMarketType === 14) {
      return (
        <Row>
          <Col xs={4} className="textTitle">
            1
          </Col>
          <Col xs={4} className="textTitle">
            X
          </Col>
          <Col xs={4} className="textTitle">
            2
          </Col>
        </Row>
      );
    }
    if (selectedMarketType === 18) {
      return (
        <Row>
          <Col xs={4} className="textTitle"></Col>
          <Col xs={4} className="textTitle">
            +
          </Col>
          <Col xs={4} className="textTitle">
            -
          </Col>
        </Row>
      );
    }
    if (selectedMarketType === 1 || selectedMarketType === 7) {
      return (
        <Row>
          <Col xs={4} className="textTitle">
            1
          </Col>
          <Col xs={4} className="textTitle">
            X
          </Col>
          <Col xs={4} className="textTitle">
            2
          </Col>
        </Row>
      );
    }
  }

  function fillLeagues() {
    let leagues = [];

    // Outrights tile at the start of the leagues bar.
    const outrightsIcon =
      "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='30' height='25' viewBox='0 0 24 24' fill='none'><path d='M6 21V11M12 21V6M18 21v-7' stroke='black' stroke-width='1.8' stroke-linecap='round'/><path d='M4 4l3 3 4-4 4 4 5-5' stroke='black' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' fill='none'/><circle cx='20' cy='2' r='1.6' fill='black'/></svg>";
    leagues.push(
      <Link to="/outrights" className="league" key="outrights-tile">
        <img width="30" height="25" src={outrightsIcon} alt="Outrights" />
        <br />
        <div style={{ fontSize: 9, fontWeight: 700, color: 'black' }}>
          {t('outrights.label')}
        </div>
      </Link>
    );

    TopLeaguesImages.map((league) => {
      let topLeagueItem;
      topLeagueItem = (
        <div
          key={league.betradarCategoryId}
          onClick={() => {
            leagueSelected(league.betradarCategoryId);
          }}
          className="league">
          {<img width="30" height="25" src={league.imageName} />}
          <br />
          {
            <div
              className={`${league.betradarCategoryId === selectedLeague ? 'league-selected' : ''}`}
              style={{ fontSize: 9, fontWeight: 700, color:'black' }}>
              {league.leagueName}
            </div>
          }
        </div>
      );
      leagues.push(topLeagueItem);
    });
    return leagues;
  }

  async function fetchData() {
    getCounts();
    dispatch(fetchLiveMatches());
    dispatch(fetchBalance());
    getPreMatches(page, sport);
  }

  useEffect(() => {
    // if (loading) return;
    const groups = eventsToGroup(liveEvents);
    dispatch(setLiveSports(groups));
    if (sport === '' && liveEvents.length > 0) {
      const type =
        selectedSport !== null || sports.hasOwnProperty(Number.parseInt(selectedSport)) === true
          ? selectedSport
          : Object.keys(groups)[0];

      dispatch(setSport(type));
    }
  }, [sport, liveEvents]);

  useEffect(() => {
    if (sport !== '') {
      dispatch(setSelectedSport(sport));
    }
  }, [sport]);

  useEffect(() => {
    console.log('page', page);
    if (page !== 0) {
      document.querySelector('.scrollable-y').scrollTop = lastScrollPosition;
      return;
    }
    getPreMatches(0);
  }, []);

  useEffect(() => {
    if (selectedLeague === '') return;
    dispatch(setPage(0));
    dispatch(setNextPageOnProgress(false));
    dispatch(setSport(''));
    getLeagueMatches();
  }, [selectedLeague]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    fetchData();
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <Container fluid className="g-0">
      <InfiniteLoadComponent
        nextPage={() => {
          nextPage(page);
        }}
        className="scrollable-y"
        style={{ height: '100vh' }}>
        <div className="scrollmenuFullWidth blackBgColor">{calculateMenuItemsDynamic()}</div>
        <div className="leagues">{fillLeagues()}</div>
        {sport === '0' && (
          <>
            <Row>
              <Col className="tip-types-container">{getBetType()}</Col>
            </Row>
            <Row>
              <Col>
                {selectedLeague != '' && (
                  <Form.Select onChange={selectLeague} value={selectedLeague}>
                    {tournamentGroups[selectedLeague.split(':')[2]].map((group) => (
                      <option key={group} id={group} value={group}>
                        {tournamentNames[group]}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </Col>
              <Col>{getBetTypeTitle(selectedMarketType)}</Col>
            </Row>
          </>
        )}

        {sport !== '0' && checkSportTypes(sport) && (
          <>
            {/* <Row>
              <Col className="tip-types-container"></Col>
            </Row> */}
            <Row>
              <Col></Col>
              <Col>
                <Row>
                  <Col xs={2}></Col>
                  <Col xs={5} className='textTitle' style={{textAlign: 'end' }}>
                    1
                  </Col>
                  <Col xs={5} className='textTitle'>
                    2
                  </Col>
                </Row>
              </Col>
            </Row>
          </>
        )}

        {sport !== '0' && !checkSportTypes(sport) && (
          <>
            {/* <Row>
              <Col className="tip-types-container"></Col>
            </Row> */}
            <Row>
              <Col></Col>
              <Col>
                <Row>
                  <Col xs={4} className='textTitle' style={{ textAlign: 'end' }}>
                    1
                  </Col>
                  <Col xs={4} className='textTitle'>
                    0
                  </Col>
                  <Col xs={4} className='textTitle' style={{textAlign: 'justify' }}>
                    2
                  </Col>
                </Row>
              </Col>
            </Row>
          </>
        )}

        <section className="mb-3">
          {selectedLeague != '' &&
            leagueMatches &&
            leagueMatches.length > 0 &&
            getBets(leagueMatches)}

          {selectedLeague === '' &&
            preMatches &&
            preMatches.length > 0 &&
            getBets(preMatches, false)}
        </section>
      </InfiniteLoadComponent>
    </Container>
  );
}

const InfiniteLoadComponent = (props) => {
  const node = useRef();
  const [lastScrollPosition, setLastScrollPosition] = useState(
    localStorage.getItem('scrollPosition') || 0
  );
  const lastScrollRef = useRef(lastScrollPosition);

  useEffect(() => {
    lastScrollRef.current = lastScrollPosition;
  }, [lastScrollPosition]);

  useEffect(() => {
    const savedScrollPosition = localStorage.getItem('lastScrollPosition');
    if (savedScrollPosition && node.current) {
      setTimeout(() => {
        node.current.scrollTop = parseInt(savedScrollPosition);
        // Clear the saved position after restoring
        localStorage.removeItem('lastScrollPosition');
      }, 100);
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (!node.current) return;
    const { scrollTop, scrollHeight, clientHeight } = node.current;

    const stickyEl = document.querySelector('.tip-types');
    if (!stickyEl) return;
    const direction = scrollTop < lastScrollRef.current ? 'up' : 'down';
    setLastScrollPosition(scrollTop);
    localStorage.setItem('scrollPosition', scrollTop);

    if (scrollTop > 110) {
      stickyEl.classList.add('fixed');
      if (direction === 'down') {
        if (scrollTop > 200) {
          stickyEl.classList.remove('fixed');
        }
      } else {
        if (scrollTop > 200) {
          stickyEl.classList.add('fixed');
        }
      }
    } else {
      stickyEl.classList.remove('fixed');
    }

    const currentPosition = window.pageYOffset || scrollTop;
    setLastScrollPosition(currentPosition);
    if (clientHeight + scrollTop >= scrollHeight - 100) {
      props.nextPage();
    }
  }, [props]);

  useEffect(() => {
    const currentNode = node.current;
    if (currentNode) {
      currentNode.addEventListener('scroll', handleScroll);
      return () => {
        currentNode.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  return (
    <div {...props} ref={node}>
      {props.children}
    </div>
  );
};

export default Start;
